import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { hideScrollbar } from 'styles/common';
import { showToast } from 'utils/modal';
import { SearchIcon } from '@/icons/SearchIcon';
import { ClearButton } from '@/ui-components/common/ClearButton';

type SearchInputProps = {
  onSearch: (keyword: string) => void;
  keyword: string;
  setKeyword: (keyword: string) => void;
};

export const SearchInput = ({ onSearch, keyword, setKeyword }: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!keyword) {
      inputRef?.current?.focus();
    }
  }, [inputRef, keyword]);

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') {
        setKeyword(inputRef.current?.value || '');
        return;
      }

      if (!inputRef.current?.value) {
        showToast('검색어나 ‘#키워드’를 입력해보세요.');
      } else {
        inputRef?.current?.blur();
        onSearch(inputRef.current?.value || '');
      }
    },
    [onSearch, setKeyword],
  );

  const handleCancle = useCallback(() => {
    setKeyword('');
  }, [setKeyword]);

  return (
    <StyledWrapper>
      <SearchIcon color={Color.Gray400} />
      <div className="flex flex-row w-full items-center justify-between">
        <StyledInput
          ref={inputRef}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyUp={handleKeyUp}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
          placeholder="검색어나 ‘#키워드’를 검색해보세요."
          value={keyword}
          autoFocus={!keyword}
        />
        {keyword && isFocused && <ClearButton onClick={handleCancle} onMouseDown={e => e.preventDefault()} />}
      </div>
    </StyledWrapper>
  );
};

SearchInput.displayName = 'Input';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
  width: 100%;
  height: 42px;
  padding: 0 10px;
  background-color: ${Color.Gray200}80; // background color opacity 0.5
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0;
  border: none;
  background-color: transparent;
  margin-left: 10px;

  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};

  ${hideScrollbar};

  &::placeholder {
    color: ${Color.Gray500};
  }

  &:focus {
    outline: none;
  }
`;
